import * as React from "react";

import CheckCircle from "@material-ui/icons/CheckCircle";
import Table from "@material-ui/core/Table/Table";
import TableBody from "@material-ui/core/TableBody/TableBody";
import TableCell from "@material-ui/core/TableCell/TableCell";
import TableHead from "@material-ui/core/TableHead/TableHead";
import TableRow from "@material-ui/core/TableRow/TableRow";
import Typography from "@material-ui/core/Typography";
import Warning from "@material-ui/icons/Warning";

import CurrencyNumber from "../../../../components/UI/CurrencyNumber";
import DateCheckmark from "../../../../components/UI/DateCheckmark";
import DateRange from "../../../../components/Forms/DateRange";

import { formatDatetime } from "../../../../utilities/format";
import { isBasicView } from "../../../../utilities/access";
import { withinDateRange } from "../../../../utilities/filter";

import {
  AccountNumber,
  PrimaryActionButton,
  TableHeadRow,
  Trash,
} from "./components";

import { getSorting, stableSort } from "../../../../utilities/sorting";
import Checkbox from "@material-ui/core/Checkbox";
import {CyManagerTypes} from "../../../../utilities/managerTypes";

export default class HouseholdPortfolioTable extends React.Component {
  state = {
    filterOnboarded: {
      from: null,
      to: null,
    },
  };

  getColumns = () => {
    const { adminView, accessLevel } = this.props;


    return [
      {
        id: "name",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Portfolio Name",
      },
      {
        id: "metadata.initialInvestment",
        numeric: false,
        sortable: true,
        label: "Investment Amount",
      },
      {
        id: "metadata.displayName",
        numeric: false,
        sortable: true,
        label: "Strategy Selection",
      },
      !isBasicView(accessLevel) && {
        id: "AccountNumber",
        numeric: false,
        sortable: false,
        label: "Account Number",
      },
      !isBasicView(accessLevel) && {
        id: "groupId",
        numeric: false,
        sortable: true,
        label: "Group ID",
      },
      !isBasicView(accessLevel) && {
        id: "onboardedAtDate",
        numeric: false,
        sortable: true,
        label: "Onboard Date",
      },
      {
        id: "createdAt",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Created",
      },
      {
        id: "updatedAt",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Last Modified",
      },
      !isBasicView(accessLevel) && {
        id: "onboardStatus",
        numeric: false,
        sortable: true,
        disablePadding: true,
        label: "Status",
      },
      adminView && {
        id: "portfolioScenario",
        numeric: false,
        sortable: false,
        disablePadding: true,
        label: "Scenario",
      },
      {
        id: "portfolioAction",
        numeric: false,
        sortable: false,
        disablePadding: true,
        label: "",
      },
    ];
  };

  renderPortfolioList = (portfolios) => {
    const { extraOrdering } = this.state;

    const {
      classes,
      custodianMap,
      history,
      household,
      order,
      orderBy,
      pathname,
      bundledPortfolios,
      handleBundlePortfolio,
      adminView,
      accessLevel,
      appName
    } = this.props;

    const data = stableSort(
      this.applyFilters(portfolios),
      getSorting(order, orderBy, extraOrdering)
    );

    const PortfolioStatus = ({portfolio, custodian}) => {
      const Grats = ({ children }) => (
        <span>
          <CheckCircle
            color="primary"
            style={{
              verticalAlign: "middle",
              marginRight: "0.2em",
            }}
          />
          {children}
        </span>
      );
      const Warn = ({ children }) => (
        <span>
          <Warning
            color="secondary"
            style={{
              verticalAlign: "middle",
              marginRight: "0.2em",
            }}
          />
          {children}
        </span>
      );
      /*
       * Special handling for American Portfolio who uses pershing llc
       */
      switch (portfolio.onboardStatus) {
        case "in-review":
          return null;
        case "forms-generated":
          if (custodian && !custodian.onboardingRules.digitalOnboardingEnabled) {
            return <Warn>Pending Paperwork from {appName}</Warn>;
          }
          return <Warn>Pending Form Completion</Warn>;
        case "forms-submitted":
          return <Warn>Pending Signatures</Warn>;
        case "forms-signed":
          return <Warn>Pending Confirmation</Warn>;
        case "onboarded":
          return <Grats>Onboarded</Grats>;
        default:
          return null;
      }
    };

    return data.map((portfolio, i) => {
      const custodian = custodianMap[portfolio.CustodianId];
      // TODO change the logic here for rendering checkbox by checking if portfolio has a custodian Id
      // Will need to migrate the data to append custodian ID on all onboarded portfolios
      const isStrategyChange = !!portfolio.CustodianId;
      return (
        <TableRow hover tabIndex={-1} key={`portfolio-${portfolio.uuid}`}>
          {!adminView && (
            <TableCell>
              {portfolio.onboardStatus === "in-review" &&
                !CyManagerTypes.disabledOnboarding(portfolio.metadata) &&
                !portfolio.onboarded && (
                  <Checkbox
                    checked={bundledPortfolios.some(
                      (el) => el.uuid === portfolio.uuid
                    )}
                    onChange={handleBundlePortfolio(portfolio)}
                    color="primary"
                    disabled={
                      (bundledPortfolios.length >= 4 &&
                        !bundledPortfolios.some(
                          (el) => el.uuid === portfolio.uuid
                        )) ||
                      isStrategyChange
                    }
                  />
                )}
            </TableCell>
          )}
          <TableCell>
            <Typography>{portfolio.name}</Typography>
          </TableCell>
          <TableCell>
            <Typography>
              {portfolio.metadata && (
                <CurrencyNumber value={portfolio.metadata.initialInvestment} />
              )}
            </Typography>
          </TableCell>
          <TableCell>
            <Typography>{portfolio.metadata.displayName}</Typography>
          </TableCell>
          {!isBasicView(accessLevel) ? (
            <React.Fragment>
              <TableCell className={classes.nowrap}>
                <AccountNumber
                  classes={classes}
                  custodian={custodian}
                  portfolio={portfolio}
                />
              </TableCell>
              <TableCell>
                {portfolio.groupId}
              </TableCell>
              <TableCell>
                {portfolio.onboarded && (
                  <DateCheckmark date={portfolio.onboardedAtDate} />
                )}
              </TableCell>
            </React.Fragment>
          ) : (
            <React.Fragment>
              <TableCell />
              <TableCell />
              <TableCell />
            </React.Fragment>
          )}
          <TableCell>{formatDatetime(portfolio.createdAt)}</TableCell>
          <TableCell>{formatDatetime(portfolio.updatedAt)}</TableCell>
          <TableCell>
            <PortfolioStatus portfolio={portfolio} custodian={custodian} />
          </TableCell>
          {adminView && (
            <TableCell>
              <Typography>
                {portfolio.metadata && portfolio.metadata.scenario}
              </Typography>
            </TableCell>
          )}
          <TableCell className={classes.nowrap}>
            <PrimaryActionButton
              history={history}
              household={household}
              adminView={adminView}
              pathname={pathname}
              portfolio={portfolio}
              custodian={custodian}
            />
            <Trash
              archivePortfolio={this.props.archivePortfolio}
              adminView={adminView}
              portfolio={portfolio}
            />
          </TableCell>
        </TableRow>
      );
    });
  };

  applyFilters = (portfolios = []) => {
    return portfolios.filter((portfolios) => {
      const date = portfolios.onboardedAtDate;
      const range = this.state.filterOnboarded;

      // need to remove missing dates when range is provided
      if (range.from && range.to && !date) {
        return false;
      }

      return !date || withinDateRange(date, range);
    });
  };

  /* Return a message in the table row when no portfolio is created */
  renderMessageForNoPortfolio = () => {
    const { classes } = this.props;
    const columns = this.getColumns();
    return (
      <TableRow>
        <TableCell colSpan={columns.length} className={classes.center}>
          No portfolios created yet
        </TableCell>
      </TableRow>
    );
  };

  render() {
    const {
      classes,
      householdPortfolios,
      handleSort,
      order,
      orderBy,
      adminView,
    } = this.props;

    const portfolios = householdPortfolios.filter(
      (p) => p.successorPortfolioId === null
    );

    return (
      <React.Fragment>
        <div className={classes.dateRangeWrapper}>
          <Typography>Search by Portfolio Onboarded Date:</Typography>
          <DateRange
            onChange={(dateRange) =>
              this.setState({ filterOnboarded: dateRange })
            }
          />
        </div>
        <section>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <TableHead>
              <TableHeadRow
                adminView={adminView}
                handleSort={handleSort}
                classes={classes}
                columns={this.getColumns()}
                order={order}
                orderBy={orderBy}
              />
            </TableHead>
            <TableBody>
              {this.applyFilters(portfolios).length > 0
                ? this.renderPortfolioList(portfolios)
                : this.renderMessageForNoPortfolio()}
            </TableBody>
          </Table>
        </section>
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/containers/HouseholdDetail/components/PortfolioTable/index.jsx